/**
 * <s-count-up> — Counts a number up from 0 to its final value the first time it scrolls into view.
 *
 * The server renders the final value as text, so shoppers without JavaScript, crawlers and
 * shoppers who prefer reduced motion always see the real number with no animation.
 *
 * Attributes:
 *   target   — Final integer value (required), e.g. "85".
 *   suffix   — Text appended after the number, e.g. "%".
 *   duration — Animation length in ms (default 1200).
 *
 * @example
 * <s-count-up target="85" suffix="%">85%</s-count-up>
 */
class SCountUp extends HTMLElement {
  #observer;
  #frame;

  connectedCallback() {
    const target = parseInt(this.getAttribute('target') ?? '', 10);
    if (!Number.isFinite(target)) return;
    if (matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    if (!('IntersectionObserver' in window)) return;

    const suffix = this.getAttribute('suffix') ?? '';
    const duration = parseInt(this.getAttribute('duration') ?? '', 10) || 1200;

    this.#observer = new IntersectionObserver(
      (entries) => {
        if (!entries.some((entry) => entry.isIntersecting)) return;
        this.#observer?.disconnect();
        this.#run(target, suffix, duration);
      },
      { threshold: 0.6 }
    );

    // Start from 0 so the count-up is visible once the shopper reaches the section.
    this.textContent = `0${suffix}`;
    this.#observer.observe(this);
  }

  disconnectedCallback() {
    this.#observer?.disconnect();
    cancelAnimationFrame(this.#frame);
  }

  #run(target, suffix, duration) {
    const start = performance.now();
    const step = (now) => {
      const progress = Math.min(1, (now - start) / duration);
      // Ease-out cubic: fast start, gentle landing on the final value.
      const eased = 1 - Math.pow(1 - progress, 3);
      this.textContent = `${Math.round(target * eased)}${suffix}`;
      if (progress < 1) this.#frame = requestAnimationFrame(step);
    };
    this.#frame = requestAnimationFrame(step);
  }
}

customElements.define('s-count-up', SCountUp);
